import React, { useEffect, useState, useMemo } from 'react';
import { Building, Plus, Edit, Trash2, Users, CreditCard, Calendar, Coins } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Modal, Input, Label, Spinner, Column } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { ViewAsOrgButton } from '@/components/admin/ViewAsOrgButton';
import { PendingRegistrationsCard } from '@/components/admin/PendingRegistrationsCard';
import { TokenManagementDialog } from '@/components/admin/TokenManagementDialog';
import { planConfigFor } from '@/components/settings/OrgSettingsPanel';
import { getAdminPlans, type PlanRecord } from '@/services/billingService';
import { getSharedProfiles, allSharedGroups } from '@/services/esimService';
import { inviteWireCustomer } from '@/services/tokenAdminService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useNavigate } from 'react-router-dom';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { PLAN_CONFIG } from '@/components/settings/OrgSettingsPanel';
import { toDate } from '@/lib/date-utils';

type PlanType = 'starter' | 'prototyper' | 'business' | 'enterprise' | 'trial' | (string & {});
type StatusType = 'active' | 'trial' | 'expired' | 'past_due' | 'canceled';

interface Organization {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  subscription?: {
    plan?: PlanType;
    status?: StatusType;
    billingPeriodStart?: any;
    billingPeriodEnd?: any;
    tokenBalance?: number;
    allowOverage?: boolean;
  };
  settings?: {
    defaults?: { eSimProvisioningMethod?: string };
    features?: { esimLpacProvisioning?: boolean; voiceCallRecording?: boolean; canUseSharedEsims?: boolean; sharedEsimGroups?: string[]; connectivityModes?: boolean };
    ownDeviceTokenRate?: number;
  };
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  pendingContactEmail?: string;
  pendingContactName?: string;
  _memberCount?: number;
}

const STATUS_CONFIG: Record<string, { label: string; variant: 'success' | 'default' | 'destructive' | 'warning' }> = {
  active:    { label: 'Active',    variant: 'success' },
  trial:     { label: 'Trial',     variant: 'default' },
  expired:   { label: 'Expired',   variant: 'destructive' },
  past_due:  { label: 'Past due',  variant: 'warning' },
  canceled:  { label: 'Canceled',  variant: 'destructive' },
};

// Filter column configuration
const filterColumnHelper = createColumnConfigHelper<Organization>();

const filterColumnsConfig = [
  filterColumnHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Name')
    .icon(Building)
    .build(),
  filterColumnHelper
    .option()
    .id('plan')
    .accessor((row) => row.subscription?.plan || 'starter')
    .displayName('Plan')
    .icon(CreditCard)
    .options([
      { label: 'Starter',    value: 'starter' },
      { label: 'Business',   value: 'business' },
      { label: 'Enterprise', value: 'enterprise' },
    ])
    .build(),
  filterColumnHelper
    .number()
    .id('memberCount')
    .accessor((row) => row._memberCount || 0)
    .displayName('Members')
    .icon(Users)
    .min(0)
    .max(100)
    .build(),
  filterColumnHelper
    .option()
    .id('createdAt')
    .accessor((row) => {
      if (!row.createdAt) return 'all';
      const now = new Date();
      const created = row.createdAt instanceof Date ? row.createdAt : new Date(row.createdAt);
      const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays <= 1) return '24h';
      if (diffDays <= 7) return '7d';
      if (diffDays <= 30) return '30d';
      if (diffDays <= 90) return '90d';
      return 'older';
    })
    .displayName('Created')
    .icon(Calendar)
    .options([
      { label: 'Last 24 hours', value: '24h' },
      { label: 'Last 7 days',   value: '7d' },
      { label: 'Last 30 days',  value: '30d' },
      { label: 'Last 90 days',  value: '90d' },
      { label: 'Older',         value: 'older' },
    ])
    .build(),
] as const;

export function OrganizationsPage() {
  const { isSuperAdmin } = useAuth();
  const { success, error: showError } = useToast();
  const fmt = useDateFormat();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [tokenDialogOrg, setTokenDialogOrg] = useState<Organization | null>(null);
  const [plans, setPlans] = useState<PlanRecord[]>([]);

  // Invite wire customer modal
  const [wireOpen, setWireOpen] = useState(false);
  const [wireForm, setWireForm] = useState({ email: '', name: '', orgName: '', plan: '', interval: 'year' as 'month' | 'year', amountUsd: '' });
  const [wireBusy, setWireBusy] = useState(false);

  // Create form state
  const [createName, setCreateName] = useState('');
  const [createOwnerEmail, setCreateOwnerEmail] = useState('');
  const [createPlan, setCreatePlan] = useState<PlanType>('starter');
  const [createStatus, setCreateStatus] = useState<StatusType>('trial');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editPlan, setEditPlan] = useState<PlanType>('starter');
  const [editStatus, setEditStatus] = useState<StatusType>('trial');
  const [editEsimDefault, setEditEsimDefault] = useState<'' | 'sgp32' | 'lpac_at' | 'manual'>('');
  const [editEsimLpacEnabled, setEditEsimLpacEnabled] = useState(true);
  const [editVoiceRecordingEnabled, setEditVoiceRecordingEnabled] = useState(true);
  const [editConnectivityModesEnabled, setEditConnectivityModesEnabled] = useState(false);
  const [editSharedEsimGroups, setEditSharedEsimGroups] = useState<string[]>([]);
  const [availableSharedGroups, setAvailableSharedGroups] = useState<{ groupId: string; groupName: string }[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: organizations,
    columnsConfig: filterColumnsConfig,
  });

  const filteredOrganizations = useMemo(() =>
    applyFilters(organizations, filters, filterColumnsConfig),
    [organizations, filters]
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    loadOrganizations();
    getAdminPlans().then(setPlans).catch((e) => console.warn('[OrganizationsPage] load plans failed:', e));
    getSharedProfiles()
      .then((profiles) => setAvailableSharedGroups(allSharedGroups(profiles)))
      .catch((e) => console.warn('[OrganizationsPage] load shared eSIM groups failed:', e));
  }, [isSuperAdmin]);

  // Plan <option>s for the create/edit dropdowns — driven by the catalog
  // (incl. custom_<slug> enterprise plans) so admins can assign any plan.
  const renderPlanOptions = () =>
    plans.length > 0
      ? plans.map((p) => (
          <option key={p.id} value={p.id}>
            {p.displayName || p.id}{p.displayName && p.displayName !== p.id ? ` (${p.id})` : ''}
          </option>
        ))
      : [
          <option key="prototyper" value="prototyper">Prototyper</option>,
          <option key="business" value="business">Business</option>,
          <option key="enterprise" value="enterprise">Enterprise</option>,
        ];

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const { getFirestore, collection, getDocs, query, orderBy } = await import('firebase/firestore');
      const db = getFirestore();

      const orgsQuery = query(collection(db, 'organizations'), orderBy('createdAt', 'desc'));
      const orgsSnapshot = await getDocs(orgsQuery);

      const orgsData: Organization[] = [];
      for (const docSnap of orgsSnapshot.docs) {
        const data = docSnap.data();
        const membersSnapshot = await getDocs(collection(db, 'organizations', docSnap.id, 'members'));
        orgsData.push({
          id: docSnap.id,
          name: data.name,
          createdAt: toDate(data.createdAt) || new Date(),
          updatedAt: toDate(data.updatedAt) || new Date(),
          subscription: data.subscription,
          settings: data.settings,
          approvalStatus: data.approvalStatus,
          pendingContactEmail: data.pendingContactEmail,
          pendingContactName: data.pendingContactName,
          _memberCount: membersSnapshot.size,
        });
      }

      setOrganizations(orgsData);
    } catch (error) {
      console.error('Failed to load organizations:', error);
      showError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!createName.trim()) {
      setCreateError('Organization name is required');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      // Go through the backend so the org gets proper membership records, and — when
      // an owner email is given — the owner is INVITED through the standard
      // registration flow (terms acceptance enforced on /signup), not created ownerless.
      const { createOrganization } = await import('@/services/tokenAdminService');
      const ownerEmail = createOwnerEmail.trim();
      const r = await createOrganization({
        name: createName.trim(),
        plan: createPlan,
        status: createStatus,
        ...(ownerEmail ? { ownerEmail } : {}),
      });

      success(
        r.invited
          ? `Organization "${createName}" created — invitation emailed to ${ownerEmail}`
          : `Organization "${createName}" created successfully!`
      );
      setIsCreateModalOpen(false);
      setCreateName('');
      setCreateOwnerEmail('');
      setCreatePlan('starter');
      setCreateStatus('trial');
      setCreateError(null);
      await loadOrganizations();
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      setCreateError(error.message || 'Failed to create organization');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditClick = (org: Organization) => {
    setSelectedOrg(org);
    setEditName(org.name);
    setEditPlan((org.subscription?.plan || 'starter') as PlanType);
    setEditStatus((org.subscription?.status || 'trial') as StatusType);
    setEditEsimDefault((org.settings?.defaults?.eSimProvisioningMethod || '') as any);
    setEditEsimLpacEnabled(org.settings?.features?.esimLpacProvisioning !== false);
    setEditVoiceRecordingEnabled(org.settings?.features?.voiceCallRecording !== false);
    setEditConnectivityModesEnabled(org.settings?.features?.connectivityModes === true);
    const grantedGroups = org.settings?.features?.sharedEsimGroups;
    if (Array.isArray(grantedGroups)) {
      setEditSharedEsimGroups(grantedGroups);
    } else if (org.settings?.features?.canUseSharedEsims === true) {
      // Legacy boolean grant → grandfather into every available group.
      setEditSharedEsimGroups(availableSharedGroups.map((g) => g.groupId));
    } else {
      setEditSharedEsimGroups([]);
    }
    setEditError(null);
    setIsEditModalOpen(true);
  };

  const handleUpdateOrganization = async () => {
    if (!editName.trim() || !selectedOrg) {
      setEditError('Organization name is required');
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const { getFirestore, doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const db = getFirestore();

      await updateDoc(doc(db, 'organizations', selectedOrg.id), {
        name: editName.trim(),
        updatedAt: serverTimestamp(),
        'subscription.plan': editPlan,
        'subscription.status': editStatus,
        'settings.defaults.eSimProvisioningMethod': editEsimDefault || null,
        'settings.features.esimLpacProvisioning': editEsimLpacEnabled,
        'settings.features.voiceCallRecording': editVoiceRecordingEnabled,
        'settings.features.connectivityModes': editConnectivityModesEnabled,
        'settings.features.sharedEsimGroups': editSharedEsimGroups,
        // Keep the legacy boolean in sync so any un-migrated reader still gates correctly.
        'settings.features.canUseSharedEsims': editSharedEsimGroups.length > 0,
      });

      success(`Organization "${editName}" updated successfully!`);
      setIsEditModalOpen(false);
      setSelectedOrg(null);
      setEditError(null);
      await loadOrganizations();
    } catch (error: any) {
      console.error('Failed to update organization:', error);
      setEditError(error.message || 'Failed to update organization');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (org: Organization) => {
    setSelectedOrg(org);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrg) return;

    setDeleteLoading(true);

    try {
      // Go through the backend so deletion CASCADES (members + their mirrors +
      // pending invitations). A client-side deleteDoc only removed the org doc,
      // leaving dangling memberships that strand those users at login.
      const { apiRequest } = await import('@/services/api');
      await apiRequest(`/organizations/${selectedOrg.id}`, { method: 'DELETE' });

      success(`Organization "${selectedOrg.name}" deleted successfully!`);
      setIsDeleteModalOpen(false);
      setSelectedOrg(null);
      await loadOrganizations();
    } catch (error: any) {
      console.error('Failed to delete organization:', error);
      showError(error.message || 'Failed to delete organization');
      setIsDeleteModalOpen(false);
      setSelectedOrg(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: Column<Organization>[] = [
    {
      key: 'name',
      header: 'Organization Name',
      sortable: true,
      render: (value, org) => (
        <div className="font-medium text-foreground">{org.name}</div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      sortable: true,
      render: (value, org) => {
        const cfg = planConfigFor(org.subscription?.plan);
        return <Badge className={cfg.className}>{cfg.label}</Badge>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (value, org) => {
        const status = org.subscription?.status;
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {status && STATUS_CONFIG[status]
              ? <Badge variant={STATUS_CONFIG[status].variant}>{STATUS_CONFIG[status].label}</Badge>
              : <span className="text-muted-foreground text-sm">—</span>}
            {org.approvalStatus === 'pending' && <Badge variant="warning">Pending</Badge>}
            {org.approvalStatus === 'rejected' && <Badge variant="destructive">Rejected</Badge>}
          </div>
        );
      },
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
      render: (value, org) => (
        <div className="flex items-center gap-1 text-muted-foreground">
          <Users className="w-4 h-4" />
          <span>{org._memberCount || 0}</span>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (value, org) => (
        <span className="text-sm text-muted-foreground">
          {fmt.formatDate(org.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (value, org) => (
        <div className="flex gap-2">
          <ViewAsOrgButton organizationId={org.id} organizationName={org.name} />
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setTokenDialogOrg(org); }}
          >
            <Coins className="w-3 h-3" />
            Tokens
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleEditClick(org); }}
          >
            <Edit className="w-3 h-3" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleDeleteClick(org); }}
          >
            <Trash2 className="w-3 h-3 text-status-error" />
          </Button>
        </div>
      ),
    },
  ];

  const handleInviteWire = async () => {
    if (!wireForm.email.trim() || !wireForm.plan.trim()) {
      showError('Email and plan are required');
      return;
    }
    const amt = wireForm.amountUsd.trim() === '' ? undefined : Number(wireForm.amountUsd);
    setWireBusy(true);
    try {
      const r = await inviteWireCustomer({
        email: wireForm.email.trim(),
        name: wireForm.name.trim() || undefined,
        orgName: wireForm.orgName.trim() || undefined,
        plan: wireForm.plan.trim(),
        interval: wireForm.interval,
        ...(amt !== undefined ? { amountUsd: amt } : {}),
      });
      void r;
      success(`Wire customer invited — ${wireForm.orgName.trim() || 'org'} is active on postpaid billing`);
      setWireOpen(false);
      setWireForm({ email: '', name: '', orgName: '', plan: '', interval: 'year', amountUsd: '' });
      loadOrganizations();
    } catch (e: any) {
      showError(e?.message ?? 'Failed to invite wire customer');
    } finally {
      setWireBusy(false);
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
              <Building className="w-6 h-6" />
              Organization Management
            </h1>
            <p className="text-body text-muted-foreground">
              Create and manage organizations across the platform
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setWireOpen(true)}>
              <Coins className="w-4 h-4" />
              Invite wire customer
            </Button>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4" />
              Create Organization
            </Button>
          </div>
        </div>

        <PendingRegistrationsCard
          pending={organizations.filter((o) => o.approvalStatus === 'pending')}
          onChanged={loadOrganizations}
        />

        <Card className="p-6">
          <div className="mb-4 pb-4 border-b border-border">
            <DataTableFilter
              columns={filterColumns}
              filters={filters}
              actions={actions}
              strategy={strategy}
            />
          </div>

          <DataTable
            columns={columns}
            data={filteredOrganizations}
            keyExtractor={(row) => row.id}
            searchable
            searchPlaceholder="Search organizations by name..."
            paginated
            defaultPageSize={10}
            emptyMessage="No organizations yet"
            loading={loading}
          />
        </Card>
      </div>

      {/* Create Organization Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Organization"
        description="Set up a new organization with a subscription plan."
      >
        <div className="space-y-4">
          {createError && <p className="text-status-error text-sm">{createError}</p>}

          <div>
            <Label htmlFor="create-org-name">Organization Name</Label>
            <Input
              id="create-org-name"
              type="text"
              placeholder="Acme Corporation"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="create-org-owner">Owner email <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              id="create-org-owner"
              type="email"
              placeholder="owner@company.com — leave blank to own it yourself"
              value={createOwnerEmail}
              onChange={(e) => setCreateOwnerEmail(e.target.value)}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              If set, they're emailed an invitation and complete registration (incl. accepting terms) to become owner. If blank, you own it.
            </p>
          </div>

          <div>
            <Label htmlFor="create-org-plan">Plan</Label>
            <select
              id="create-org-plan"
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              value={createPlan}
              onChange={(e) => setCreatePlan(e.target.value as PlanType)}
            >
              {renderPlanOptions()}
            </select>
          </div>

          <div>
            <Label htmlFor="create-org-status">Status</Label>
            <select
              id="create-org-status"
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              value={createStatus}
              onChange={(e) => setCreateStatus(e.target.value as StatusType)}
            >
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrganization} disabled={createLoading}>
              {createLoading ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
              {createLoading ? 'Creating...' : 'Create Organization'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Organization Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Organization"
        description="Update organization subscription and settings."
      >
        <div className="space-y-4">
          {editError && <p className="text-status-error text-sm">{editError}</p>}

          <div>
            <Label htmlFor="edit-org-name">Organization Name</Label>
            <Input
              id="edit-org-name"
              type="text"
              placeholder="Acme Corporation"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="edit-org-plan">Plan</Label>
            <select
              id="edit-org-plan"
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              value={editPlan}
              onChange={(e) => setEditPlan(e.target.value as PlanType)}
            >
              {renderPlanOptions()}
            </select>
          </div>

          <div>
            <Label htmlFor="edit-org-status">Status</Label>
            <select
              id="edit-org-status"
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as StatusType)}
            >
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div className="pt-3 border-t border-border">
            <Label htmlFor="edit-esim-default">Default eSIM provisioning method</Label>
            <select
              id="edit-esim-default"
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              value={editEsimDefault}
              onChange={(e) => setEditEsimDefault(e.target.value as any)}
            >
              <option value="">No default (manual)</option>
              <option value="sgp32">SGP.32 (cloud eIM)</option>
              <option value="lpac_at">lpac (modem AT)</option>
              <option value="manual">Manual / never</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Fallback used when a modem doesn't set its own method on the Modems tab.
              Per-modem setting always wins; device-level "Skip eSIM provisioning" overrides everything.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editEsimLpacEnabled}
                onChange={(e) => setEditEsimLpacEnabled(e.target.checked)}
                className="w-4 h-4 text-accent-blue focus:ring-accent-blue rounded"
              />
              <span>lpac-over-AT eSIM provisioning enabled</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Kill-switch for the consumer-RSP path. <strong>On</strong> by default —
              modems set to <code>lpac_at</code> work normally. Turn <strong>off</strong>
              to instant-kill the path for this org if a regression surfaces.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editVoiceRecordingEnabled}
                onChange={(e) => setEditVoiceRecordingEnabled(e.target.checked)}
                className="w-4 h-4 text-accent-blue focus:ring-accent-blue rounded"
              />
              <span>Record call audio on voice tests</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              <strong>On</strong> by default — every automated test that places a real call
              (Voice Call Check, Calling Card Check, …) saves the audio as a playable
              <code> voice_wav</code> artifact on the run. Interactive TLAM calls are never
              recorded. Requires a modem with a UAC audio path.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editConnectivityModesEnabled}
                onChange={(e) => setEditConnectivityModesEnabled(e.target.checked)}
                className="w-4 h-4 text-accent-blue focus:ring-accent-blue rounded"
              />
              <span>Connectivity modes (persistent attach / PLMN hunt)</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              <strong>Off</strong> by default — opt-in. When on, org owners/admins can put a modem on one of
              their <strong>private</strong> edges into persistent-attach or PLMN-hunt mode from the device
              Modems tab. Shared edges and the backhaul modem are always refused. The platform-wide kill switch
              on System Settings overrides this flag.
            </p>
          </div>

          <div>
            <Label className="text-sm">Shared eSIM groups</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">
              Grant this org access to specific super-admin <strong>shared eSIM inventory</strong>
              {' '}groups. Each granted group appears as a "SimCheck Shared — <em>name</em>" entry in the
              org's eSIM pickers; runs land on the modem that holds the resident SIM (no download).
              Leave all unchecked to deny shared eSIMs entirely.
            </p>
            {availableSharedGroups.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No shared eSIM groups exist yet. Promote a resident SIM to a shared group from an
                edge device's eUICC inventory first.
              </p>
            ) : (
              <div className="space-y-1.5">
                {availableSharedGroups.map((g) => {
                  const checked = editSharedEsimGroups.includes(g.groupId);
                  return (
                    <label key={g.groupId} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setEditSharedEsimGroups((prev) =>
                            e.target.checked
                              ? [...new Set([...prev, g.groupId])]
                              : prev.filter((x) => x !== g.groupId),
                          )
                        }
                        className="w-4 h-4 text-accent-blue focus:ring-accent-blue rounded"
                      />
                      <span>{g.groupName}</span>
                      <span className="text-xs text-muted-foreground">({g.groupId})</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOrganization} disabled={editLoading}>
              {editLoading ? <Spinner size="sm" /> : <Edit className="w-4 h-4" />}
              {editLoading ? 'Updating...' : 'Update Organization'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Organization"
        description="Are you sure you want to delete this organization? This action cannot be undone."
        size="sm"
      >
        <div className="space-y-4">
          {selectedOrg && (
            <div className="p-4 bg-status-error-bg border border-status-error rounded">
              <p className="text-sm text-status-error">
                You are about to permanently delete <strong>{selectedOrg.name}</strong> and all its associated data.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteOrganization}
              disabled={deleteLoading}
            >
              {deleteLoading ? <Spinner size="sm" /> : <Trash2 className="w-4 h-4" />}
              {deleteLoading ? 'Deleting...' : 'Delete Organization'}
            </Button>
          </div>
        </div>
      </Modal>

      {tokenDialogOrg && (
        <TokenManagementDialog
          orgId={tokenDialogOrg.id}
          orgName={tokenDialogOrg.name}
          allowOverage={tokenDialogOrg.subscription?.allowOverage}
          onClose={() => setTokenDialogOrg(null)}
          onChanged={loadOrganizations}
        />
      )}

      <Modal
        isOpen={wireOpen}
        onClose={() => setWireOpen(false)}
        title="Invite wire customer"
        description="Create a new org on postpaid wire billing. The account activates immediately — no upfront invoice. They're invoiced by bank transfer (Wise) at the end of each billing cycle; service continues until you stop it. Amount is the optional negotiated per-cycle price (defaults to the plan's list price)."
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="wire-email">Billing email</Label>
            <Input id="wire-email" type="email" value={wireForm.email}
              onChange={(e) => setWireForm((f) => ({ ...f, email: e.target.value }))} placeholder="finance@customer.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="wire-name">Contact name</Label>
              <Input id="wire-name" value={wireForm.name} onChange={(e) => setWireForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="wire-org">Org name</Label>
              <Input id="wire-org" value={wireForm.orgName} onChange={(e) => setWireForm((f) => ({ ...f, orgName: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Label htmlFor="wire-plan">Plan</Label>
              <select id="wire-plan" value={wireForm.plan}
                onChange={(e) => setWireForm((f) => ({ ...f, plan: e.target.value }))}
                className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground">
                <option value="">Select…</option>
                {renderPlanOptions()}
              </select>
            </div>
            <div>
              <Label htmlFor="wire-interval">Interval</Label>
              <select id="wire-interval" value={wireForm.interval}
                onChange={(e) => setWireForm((f) => ({ ...f, interval: e.target.value as 'month' | 'year' }))}
                className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground">
                <option value="year">Annual</option>
                <option value="month">Monthly</option>
              </select>
            </div>
            <div>
              <Label htmlFor="wire-amount">Amount $ (opt)</Label>
              <Input id="wire-amount" type="number" value={wireForm.amountUsd}
                onChange={(e) => setWireForm((f) => ({ ...f, amountUsd: e.target.value }))} placeholder="plan price" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setWireOpen(false)} disabled={wireBusy}>Cancel</Button>
            <Button onClick={handleInviteWire} disabled={wireBusy}>
              {wireBusy ? <Spinner size="sm" /> : <Coins className="w-4 h-4" />}
              Invite customer
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
